import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import { applySyncPatch } from "../../../components/lifecycle/warehouseSync/applyPatch";
import { applyCountrySyncPatch } from "../../../components/lifecycle/warehouseSync/applyCountryPatch";
import { counterpartyKeys } from "../../tradingCounterparties/dataHooks/counterpartyQueries";
import { brokerKeys } from "../../brokers/dataHooks/brokerQueries";
import { countryKeys } from "../../countries/dataHooks/countryQueries";
import { ApplySyncVariables, SyncEntityType } from "../rows";

/**
 * What went wrong with one entity's apply, in terms the page can show.
 *
 * The generated client throws the Response itself on a non-2xx (see `if (!response.ok) throw data`
 * in Api.ts), so the status is both the most reliable thing we have and the one that matters,
 * because it splits the two cases a user needs to tell apart:
 *
 *   406  the update endpoint returned false, which for every book means the blob save failed.
 *        Nothing was written, so retrying is safe and is the right thing to do.
 *   5xx  something threw after the blob save: the queue enqueue and the summary upsert both happen
 *        after it. The write may well have landed, so check the entity before retrying rather than
 *        assuming the values are still missing.
 *
 * Anything with no status at all failed before the request was answered, i.e. network or auth.
 */
export const describeApplyError = (error: unknown): string => {
  const status =
    typeof error === "object" && error !== null && "status" in error
      ? (error as { status?: number }).status
      : undefined;

  if (status === 406) {
    return "406, the save was rejected and nothing was written";
  }

  if (status && status >= 500) {
    return `${status}, the save may have landed, check before retrying`;
  }

  if (status) {
    return `${status}`;
  }

  return error instanceof Error ? error.message : "no response";
};

/**
 * The query keys to refresh after a write. Not a lookup object: the three key sets are structurally
 * different types (their tuples start with their own book's name), so indexing one would need a cast
 * that hides exactly the mistake it would be covering for.
 */
const keysFor = (entityType: SyncEntityType) =>
  entityType === "broker"
    ? brokerKeys
    : entityType === "country"
      ? countryKeys
      : counterpartyKeys;

/**
 * Applies one entity's accepted warehouse differences, counterparty, broker or country.
 *
 * Refetches the entity first rather than patching the row the preview was built from: the update is
 * a full document write, so a stale copy would silently revert whatever anyone else changed since
 * the preview was loaded. The window is not closed by this (nothing here locks the document), only
 * narrowed from "since page load" to "during this request".
 *
 * The bulk page calls this once per entity, in sequence, because each update is a
 * read-modify-write on the blob plus a summary table upsert plus a queue message.
 */
export const useApplyWarehouseSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async (variables: ApplySyncVariables) => {
      const apiClient = await getAuthenticatedApiClient();
      const { id, entityType } = variables;

      if (entityType === "country") {
        const fresh = (await apiClient.countrySpecific.countrySpecific({ id }))
          .data;

        if (!fresh?.id) {
          throw new Error(`Country ${id} could not be read back`);
        }

        const updated = applyCountrySyncPatch(
          fresh,
          variables.partyPatches,
          new Date().toISOString()
        );
        await apiClient.countryUpdate.countryUpdate(updated);
        return { entityType, updated };
      }

      if (entityType === "broker") {
        const fresh = (await apiClient.brokerSpecific.brokerSpecific({ id }))
          .data;

        if (!fresh?.id) {
          throw new Error(`Broker ${id} could not be read back`);
        }

        const updated = applySyncPatch(
          fresh,
          variables.patch,
          new Date().toISOString(),
          "broker"
        );
        await apiClient.brokerUpdate.brokerUpdate(updated);
        return { entityType, updated };
      }

      const fresh = (await apiClient.specific.specific({ id })).data;

      if (!fresh?.id) {
        throw new Error(`Counterparty ${id} could not be read back`);
      }

      const updated = applySyncPatch(
        fresh,
        variables.patch,
        new Date().toISOString(),
        "counterparty"
      );
      await apiClient.update.update(updated);
      return { entityType, updated };
    },
    onSuccess: ({ entityType, updated }) => {
      // Guarded because mutateAsync rejects when a callback throws, and by the time this runs the
      // entity has already been written. Refreshing the cache is housekeeping: letting it fail the
      // mutation would report a successful save as a failed one, which is the one thing the sweep's
      // result must not get wrong.
      //
      // Only the cached entity is updated here, which costs nothing. Invalidating the overviews is
      // deliberately NOT done per entity: that is a network refetch of a whole book's summary table,
      // and a sweep over fifty entities would fire fifty of them, each racing the next entity's own
      // read and write. useRefreshSyncedOverviews does it once, after the sweep.
      try {
        const keys = keysFor(entityType);

        if (updated.id) {
          queryClient.setQueryData(keys.specific(updated.id), updated);
        }
      } catch (cacheError) {
        console.error(
          `Warehouse sync: ${entityType} ${updated.id} was saved, but refreshing the cache failed`,
          cacheError
        );
      }
    }
  });
};

/**
 * Refreshes every book's overview once, for calling after a sweep rather than after each entity.
 * All three are invalidated without checking what was actually written: the call is cheap when a
 * book has no active overview query, and tracking which books a sweep touched to save one no-op is
 * not worth the way it would go wrong.
 */
export const useRefreshSyncedOverviews = () => {
  const queryClient = useQueryClient();

  return () => {
    [counterpartyKeys, brokerKeys, countryKeys].forEach((keys) =>
      queryClient.invalidateQueries({ queryKey: keys.overviews() })
    );
  };
};
